'use client';

import { createBrowserClient } from '@supabase/ssr';

const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    // Try permissive cookie first (works in most browsers including iframes)
    try {
      document.cookie = `${k}=1; Path=/; SameSite=Lax`;
      if (document.cookie.includes(k)) {
        document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=Lax`;
        cache = true;
        return cache;
      }
    } catch {}
    // Fallback: try Partitioned cookie
    try {
      document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
      if (document.cookie.includes(k)) {
        document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
        cache = true;
        return cache;
      }
    } catch {}
    cache = false;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined' ? [] :
  document.cookie.split(';').filter(Boolean).map((c) => {
    const trimmed = c.trim();
    const eqIdx = trimmed.indexOf('=');
    const name = eqIdx >= 0 ? trimmed.slice(0, eqIdx) : trimmed;
    const value = eqIdx >= 0 ? decodeURIComponent(trimmed.slice(eqIdx + 1)) : '';
    return { name: name.trim(), value };
  }).filter((c) => c.name);

const fromStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch { return []; }
};

const setCookie = (name: string, value: string, options?: any) => {
  if (typeof document === 'undefined') return;
  // Use SameSite=Lax as default (works in most browsers without HTTPS requirement)
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=Lax`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
  // Also try Secure/Partitioned variant for cross-origin contexts
  try {
    let s2 = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
    if (options?.maxAge) s2 += `; Max-Age=${options.maxAge}`;
    document.cookie = s2;
  } catch {}
};

const deleteCookie = (name: string) => {
  if (typeof document === 'undefined') return;
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const domains = ['', host, host ? `.${host}` : ''].filter(Boolean);
  const variants = [
    'Path=/; SameSite=Lax',
    'Path=/; SameSite=None; Secure',
    'Path=/; SameSite=None; Secure; Partitioned',
  ];
  variants.forEach((attrs) => {
    document.cookie = `${name}=; Max-Age=0; ${attrs}`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; Max-Age=0; Domain=${domain}; ${attrs}`;
    });
  });
};

const getToken = () =>
  (canUseCookies() ? fromCookies() : fromStorage())
    .find((c) => c.name.includes('auth-token'))?.value ?? null;

if (typeof window !== 'undefined') {
  const setupFetchPatch = () => {
    if ((window as any).__sb_patched__) return;
    (window as any).__sb_patched__ = true;
    const orig = window.fetch.bind(window);
    window.fetch = (input, init) => {
      const token = getToken();
      const url = typeof input === 'string' ? input
        : input instanceof URL ? input.href
        : (input as Request).url;
      if (token && (url.startsWith('/') || url.startsWith(window.location.origin))) {
        init = { ...(init || {}), headers: { ...(init?.headers || {}), 'x-sb-token': token } };
      }
      return orig(input, init);
    };
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupFetchPatch);
  } else {
    setupFetchPatch();
  }
}

export function createClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      cookies: {
        getAll: () => {
          // Always try cookies first, then fall back to localStorage
          const cookieItems = fromCookies();
          const storageItems = fromStorage();
          // Merge: cookies take priority, but include storage items not in cookies
          const cookieNames = new Set(cookieItems.map(c => c.name));
          const merged = [...cookieItems, ...storageItems.filter(s => !cookieNames.has(s.name))];
          return merged;
        },
        setAll(cookiesToSet) {
          if (typeof document === 'undefined') return;
          cookiesToSet.forEach(({ name, value, options }) => {
            if (value) {
              setCookie(name, value, options);
              // Also persist to localStorage as backup
              try {
                localStorage.setItem(`${PFX}${name}`, value);
              } catch {}
            } else {
              deleteCookie(name);
              try {
                localStorage.removeItem(`${PFX}${name}`);
              } catch {}
            }
          });
        },
      },
    }
  );
}
